import { createStore, combineReducers } from 'redux'
import ssr, { initialState as ssrInitialState } from './ssr'
import collection, { initialState as collectionInitialState } from './collection'
import { HYDRATE, createWrapper } from 'next-redux-wrapper'
import { isSSR } from '@/utils/is'
import type { UserRes } from '@/types/api'
import { Partner } from '@/global'

export interface VersionData {
  version: string
  date: string
  title: string
  description: string[]
  banners: Array<{
    content: string
    buttonText: string
    buttonUrl: string
    buttonColor: string
  }>
  changelog: string
  headlines: Array<{
    background: string
    url: string
  }>
}

export interface GlobalState {
  userInfo: Partial<UserRes>
  userLoading: boolean
  version: string
  versionData: VersionData
  versionModalVisible: boolean
  versionDataLoading: boolean
  redsList: Partner[]
  productNameTranslate: boolean
  searchType: '1' | '2'
}

const initialState: GlobalState = {
  userLoading: true,
  userInfo: {},
  version: '',
  versionData: {
    version: '',
    date: '',
    title: '',
    description: [],
    banners: [],
    changelog: '',
    headlines: []
  },
  versionModalVisible: false,
  versionDataLoading: false,
  redsList: [],
  productNameTranslate: false,
  searchType: '1'
}

function global(state = initialState, action: any) {
  switch (action.type) {
    case 'update-userInfo': {
      const { userInfo = state.userInfo, userLoading = false } = action.payload
      return {
        ...state,
        userLoading,
        userInfo
      }
    }
    case 'update-version': {
      return {
        ...state,
        version: action.payload
      }
    }
    case 'update-reds-list': {
      return {
        ...state,
        redsList: action.payload
      }
    }
    case 'update-product-name-translate': {
      return {
        ...state,
        productNameTranslate: action.payload
      }
    }
    case 'update-version-data': {
      return {
        ...state,
        versionData: action.payload
      }
    }
    case 'update-version-modal-visible': {
      return {
        ...state,
        versionModalVisible: action.payload
      }
    }
    case 'update-version-data-loading': {
      return {
        ...state,
        versionDataLoading: action.payload
      }
    }
    case 'update-search-type': {
      return {
        ...state,
        searchType: action.payload
      }
    }
    default:
      return state
  }
}

export interface StoreState {
  global: GlobalState
  ssr: ReturnType<typeof ssr>
  collection: ReturnType<typeof collection>
}

const combinedReducer = combineReducers({
  global,
  ssr,
  collection
})

const reducer: typeof combinedReducer = (
  state = {
    global: initialState,
    ssr: ssrInitialState,
    collection: collectionInitialState
  },
  action
) => {
  // 仅有 ssr reducer 是在服务端渲染时产生 state ，所以在注水时仅注水 ssr reducer
  if (action.type === HYDRATE) {
    return {
      ...state,
      ssr: action.payload.ssr
    }
  } else {
    return combinedReducer(state, action)
  }
}

export const wrapper = createWrapper(() =>
  createStore(
    reducer,
    !isSSR && process.env.NODE_ENV === 'development'
      ? window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()
      : undefined
  )
)

// Action creators
export const updateProductNameTranslate = (isTranslate: boolean) => ({
  type: 'update-product-name-translate',
  payload: isTranslate
})

export const updateVersionData = (versionData: VersionData) => ({
  type: 'update-version-data',
  payload: versionData
})

export const updateVersionModalVisible = (visible: boolean) => ({
  type: 'update-version-modal-visible',
  payload: visible
})

export const updateVersionDataLoading = (loading: boolean) => ({
  type: 'update-version-data-loading',
  payload: loading
})

export const updateSearchType = (searchType: '1' | '2') => ({
  type: 'update-search-type',
  payload: searchType
})
